import { useEffect, useState } from "react";
import Avvvatars from "avvvatars-react";
import { my_routes } from "../router/all_routes";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import InfosAlertYearDisabled from "../../core/common/alerts/infosAlertYearDisabled";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import Table from "../../core/common/dataTable/index";
import { Link } from "react-router";
import { toast } from "sonner";
import {
  getAllHourlyRatesByYear,
  getAllTeachers,
  updateHourlyRate,
} from "../../api/teachers";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { Button } from "antd";
import HourlyRateCell from "./hourlyRateCell";
import TooltipOptionHourlyRate from "./tooltipOptionHourlyRate";
import CreateHourlyRateTeacherModal from "./createHourlyRateTeacher_modal";
import withPermission from "../../core/common/autorisations/withPermission";

const hourlyRateSchema = z.object({
  id_amount: z.string().optional(),
  id_academicYear: z.string(),
  id_teacherProfil: z.string(),
  amount: z.number(),
  updated_amount: z.number().optional(),
  isEditing: z.boolean().optional(),
});

const hourlyRatesSchema = z.object({
  hourlyRates: z.array(hourlyRateSchema),
});
type HourlyRatesFormData = z.infer<typeof hourlyRatesSchema>;

//  restreindre l'acces à ce composant
const ProtectedAddHourlyRate = withPermission(
  (props: any) => (
    <div className="d-flex my-xl-auto right-content align-items-center">
      <TooltipOptionHourlyRate updateData={props?.updateData} />
      <div className="mb-2">
        <Button
          type="primary"
          className="d-flex align-items-center me-3"
          onClick={props.showModal}
          disabled={props.teachersWithoutRate.length === 0}
        >
          <i className="ti ti-square-rounded-plus me-2" />
          Nouveau Tarif horaire
        </Button>
      </div>
    </div>
  ),
  [buildPermission(Resource.HOURLYRATE_TEACHER, Action.CREATE)],
  "masquer",
);

//  restreindre l'acces à ce composant
const ProtectedUpdateHourlyRate = withPermission(
  (props: any) => (
    <Button
      className="btn btn-sm btn-primary"
      disabled={!props.isDirty}
      onClick={() =>
        props.handleUpdateHourlyRate({
          ...props.record,
          updated_amount: props.updatedAmount,
        })
      }
    >
      Enregistrer
    </Button>
  ),
  [buildPermission(Resource.HOURLYRATE_TEACHER, Action.UPDATE)],
  "masquer",
);

const HourlyRateTeacher = () => {
  const routes = my_routes;
  const { selectedYear } = useAcademicYear();

  // Initialisation des state utilisés
  const [allTeachers, setAllTeachers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a pas de données
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [teachersWithoutRate, setTeachersWithoutRate] = useState<any[]>([]);

  const fetchAllHourlyRateData = async () => {
    try {
      const response = await getAllHourlyRatesByYear(selectedYear?.id);

      setAllTeachers(response.data || []);
      const formattedRates = response.data?.map((item: any) => ({
        id_amount: item.id,
        id_academicYear: item.id_academicYear,
        id_teacherProfil: item.id_teacherProfil,
        amount: Number(item.amount),
        updated_amount: undefined,
        isEditing: false,
        teacherName: `${item.TeacherProfil?.firstname || ""} ${
          item.TeacherProfil?.lastname || ""
        }`,
      }));
      replace(formattedRates || []);

      setIsLoading(false); //Les données ne sont plus en chargement
      setHasLoaded(true); //Les données ont été chargé avec succès
    } catch (error: any) {
      // console.log(
      //   "erreur: lors de la récupération des taux horaire des professeurs ",
      //   error,
      // );
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // Récupérer tous les professeurs et filtrer ceux sans taux horaire
  const fetchTeachersWithoutRate = async () => {
    try {
      const teachersResponse = await getAllTeachers(
        buildPermissionContext(Resource.TEACHER, Action.READ),
      );

      const allTeachersList = teachersResponse.data || [];
      const teachersWithRate = allTeachers.map((rate) => rate.id_teacherProfil);

      const teachersWithoutRateList = allTeachersList.filter(
        (teacher: any) => !teachersWithRate.includes(teacher.id),
      );

      setTeachersWithoutRate(teachersWithoutRateList);
    } catch (error: any) {
      // console.log(
      //   "Erreur lors de la récupération des professeurs sans taux horaire:",
      //   error,
      // );
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Erreur lors de la récupération des professeurs",
      );
    }
  };
  useEffect(() => {
    fetchAllHourlyRateData();
  }, [selectedYear]);

  // Mettre à jour la liste des professeurs sans taux horaire quand les données changent
  useEffect(() => {
    if (hasLoaded) {
      fetchTeachersWithoutRate();
    }
  }, [hasLoaded, allTeachers]);

  // Fonctions pour gérer le modal
  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleModalSuccess = () => {
    setIsModalVisible(false);
    fetchAllHourlyRateData(); // Recharger les données
  };

  // Fonction pour modifier un taux horaire
  const handleUpdateHourlyRate = async (record: any) => {
    try {
      if (!selectedYear?.id || !record.id_teacherProfil) return;
      // console.log("record", record);
      // TODO: call API to save hourly rate
      await updateHourlyRate(record.id, {
        amount: record.updated_amount ?? record.amount,
        id_academicYear: selectedYear?.id,
        id_teacherProfil: record.id_teacherProfil,
      });
      setValue(
        `hourlyRates.${record.index}.amount`,
        record.updated_amount ?? record.amount,
      );
      fetchAllHourlyRateData();
      toast.success("Taux horaire enregistré");
    } catch (error: any) {
      // console.log("erreur: lors de la modification du taux horaire ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const { control, setValue, watch } = useForm<HourlyRatesFormData>({
    resolver: zodResolver(hourlyRatesSchema),
  });

  const { fields, replace } = useFieldArray({
    control,
    name: "hourlyRates",
  });

  //   Les colonnes  de notre tableau
  const columns = [
    {
      title: "Nom",
      dataIndex: "teacherName",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center">
          <Link
            to={`/teacher-detail/${record.id}`}
            className="avatar avatar-md"
          >
            <Avvvatars
              value={`${record?.TeacherProfil?.firstname?.charAt(0) || ""}${
                record?.TeacherProfil?.lastname?.charAt(0) || ""
              }`}
              style="character" // ou "character"
            />
          </Link>
          <div className="ms-2">
            <p className="text-dark mb-0">
              <Link to={`/teacher-detail/${record.id}`}>{`${
                record?.TeacherProfil?.firstname || ""
              } ${record?.TeacherProfil?.lastname || ""}`}</Link>
            </p>
            <span className="fs-12">
              {record?.TeacherProfil?.matricule || ""}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        a.TeacherProfil?.firstname?.length - b.TeacherProfil?.firstname?.length,
    },
    {
      title: "Taux horaire",
      dataIndex: "amount",
      render: (value: any, record: any) => {
        return (
          <HourlyRateCell
            index={fields.findIndex((f) => f.id_amount === record.id)}
            watch={watch}
            setValue={setValue}
          />
        );
      },
      sorter: (a: any, b: any) => (a.amount || 0) - (b.amount || 0),
    },
    {
      title: "Action",
      key: "action",
      render: (text: any, record: any, index: number) => {
        const currentAmount = watch(`hourlyRates.${index}.amount`) ?? 0;
        const updatedAmount = watch(`hourlyRates.${index}.updated_amount`);
        const isEditing = watch(`hourlyRates.${index}.isEditing`);
        const isDirty =
          updatedAmount !== undefined &&
          updatedAmount !== currentAmount &&
          !isEditing;

        return (
          <ProtectedUpdateHourlyRate
            isDirty={isDirty}
            handleUpdateHourlyRate={handleUpdateHourlyRate}
            record={record}
            updatedAmount={updatedAmount}
          />
        );
      },
    },
  ];

  return (
    <>
      {/* Page wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Taux Horaire Professeurs</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.allTeacher}>Ensieignants</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Taux Horaire Professeurs
                  </li>
                </ol>
              </nav>
            </div>
            {/* Boutton en entête de la page */}
            <ProtectedAddHourlyRate
              routes={routes}
              updateData={fetchAllHourlyRateData}
              showModal={showModal}
              teachersWithoutRate={teachersWithoutRate}
            />
          </div>
          {/* /Page Header */}
          {!selectedYear?.statut && <InfosAlertYearDisabled />}
          {/* Tableau liste des professeurs */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Liste des professeurs/ Taux horaire</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="input-icon-start mb-3 me-2 position-relative">
                  {/* <PredefinedDateRanges /> */}
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              {/* Students List (On affiche le tableau qui si les données son chargées même vide) */}
              {isLoading && !hasLoaded ? (
                <div className="text-center">
                  <div className="spinner-border" role="status"></div>
                  <span className="visually">Chargement des Données...</span>
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={allTeachers}
                  Selection={true}
                />
              )}
              {/* /Staffs List */}
            </div>
          </div>
        </div>
      </div>

      {/* Modal pour ajouter un nouveau taux horaire */}
      <CreateHourlyRateTeacherModal
        visible={isModalVisible}
        onCancel={handleModalCancel}
        onSuccess={handleModalSuccess}
        teachersWithoutRate={teachersWithoutRate}
      />
    </>
  );
};

export default HourlyRateTeacher;
